import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader, Segmented, StatGrid, StatTile, PanelGrid, Panel, SimpleTable, RankList, BarChart, DonutChart,
  Select, Banner, Button, EmptyState,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { IS_MISHTAMERET } from '../../next/adminNav';

/**
 * דף הבית — new design. Same endpoint and range / exam filters as HomeDashboard,
 * laid out with the crm-kit/next dashboard blocks (stat tiles, panels, token-coloured charts).
 */
const RANGES = [
  { value: '7d', label: 'שבוע' },
  { value: '30d', label: '30 יום' },
  { value: '90d', label: '3 חודשים' },
  { value: '1y', label: 'שנה' },
  { value: 'all', label: 'הכל' },
];
const CITY_COLORS = ['var(--ck-primary)', 'var(--ck-success)', 'var(--ck-warning)', 'var(--ck-info)', 'var(--ck-violet)', 'var(--ck-danger)', '#c9a84c', 'var(--ck-text-3)'];
const num = (v) => Number(v || 0).toLocaleString('he-IL');

export default function HomeDashboardNext() {
  const navigate = useNavigate();
  const [range, setRange] = useState('30d');
  const [examId, setExamId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [units, setUnits] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { range };
      if (examId) params.examId = examId;
      const { data: payload } = await api.get('/reports/home-dashboard', { params });
      setData(payload);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת דף הבית');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, examId]);

  useEffect(() => { load(); }, [load]);

  // בקשה נפרדת — תקלה בימות לא מפילה את דף הבית
  useEffect(() => {
    let cancelled = false;
    api.get('/reports/yemot-units')
      .then(({ data: payload }) => { if (!cancelled) setUnits({ value: payload.units }); })
      .catch((err) => { if (!cancelled) setUnits({ error: err.response?.data?.error || 'לא ניתן לטעון מימות' }); });
    return () => { cancelled = true; };
  }, []);

  const resultsLink = useMemo(
    () => (IS_MISHTAMERET || data?.mode === 'mishtameret' ? '/american-exams' : '/reports'),
    [data?.mode],
  );
  const busy = loading && !data;
  const d = data || {};
  const trend = (rows) => (rows || []).map((r, i) => ({ key: r.label || i, label: r.label, value: Number(r.value) || 0 }));
  const cities = (d.cityBreakdown || []).map((c, i) => ({ key: c.city, label: c.city, value: Number(c.count) || 0, color: CITY_COLORS[i % CITY_COLORS.length] }));
  const ivr = d.ivrMinutes || {};

  return (
    <>
      <PageHeader title="דף הבית" sub="סקירה כללית של המערכת">
        <Segmented items={RANGES} value={range} onChange={setRange} ariaLabel="טווח זמן" />
      </PageHeader>

      {error ? (
        <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{error}</Banner>
      ) : null}

      <StatGrid min={200}>
        <StatTile
          label="נבחנים במערכת"
          value={num(d.members?.total)}
          sub={`${num(d.members?.active)} פעילים · ${num(d.members?.inactive)} לא פעילים`}
          icon="users"
          tone="primary"
          loading={busy}
          onClick={() => navigate('/members')}
        />
        <StatTile
          label="מסלולים פעילים"
          value={num(d.tracksSummary?.activeTracks)}
          sub={`${num(d.tracksSummary?.examsCompleted)} מבחנים מולאו · ${num(d.tracksSummary?.uniqueExaminees)} נבחנים`}
          icon="task"
          tone="violet"
          loading={busy}
        />
        <StatTile
          label="דקות האזנה — היום"
          value={num(ivr.today)}
          sub={`7 ימים ${num(ivr.days7)} · 30 יום ${num(ivr.days30)}`}
          icon="phone"
          tone="info"
          loading={busy}
          warn={d.ivrMinutesMeta?.connected === false}
          title={d.ivrMinutesMeta?.connected === false ? `לא ניתן לטעון מימות${d.ivrMinutesMeta.error ? ` — ${d.ivrMinutesMeta.error}` : ''}` : undefined}
        />
        <StatTile
          label="דקות האזנה — השנה"
          value={num(ivr.year)}
          sub={`3 חודשים ${num(ivr.months3)} · סה״כ ${num(ivr.total)}`}
          icon="chart"
          tone="success"
          loading={busy}
        />
        <StatTile
          label="יתרת יחידות — ימות המשיח"
          value={units?.error ? '—' : num(units?.value)}
          sub={units?.error ? 'לא ניתן לטעון מימות' : 'יתרה נוכחית'}
          icon="phone"
          tone="warning"
          loading={!units}
          warn={Boolean(units?.error)}
          title={units?.error}
        />
      </StatGrid>

      {d.ivrMinutesMeta?.connected === false ? (
        <Banner tone="warning" icon="phone">לא ניתן לטעון דקות האזנה מימות{d.ivrMinutesMeta.error ? ` — ${d.ivrMinutesMeta.error}` : ''}</Banner>
      ) : null}

      <PanelGrid cols={3}>
        <Panel title="סטטוס מסלולים" icon="list" flush>
          <SimpleTable
            loading={busy}
            rowKey="key"
            rows={d.tracks || []}
            empty="אין מסלולים"
            columns={[
              { key: 'label', label: 'מסלול', render: (t) => <b>{t.label}</b> },
              { key: 'examCount', label: "מבחנ'", align: 'center' },
              { key: 'completed', label: 'מולאו', align: 'center', render: (t) => t.completed || 0 },
              { key: 'examinees', label: 'נבחנים', align: 'center', render: (t) => t.examinees || 0 },
              { key: 'avgScore', label: 'ממוצע', align: 'center', render: (t) => (t.avgScore != null ? t.avgScore : <span className="ck-muted">—</span>) },
            ]}
          />
        </Panel>

        <Panel
          title="מבחנים אחרונים"
          icon="check"
          tone="success"
          actions={<Button size="sm" variant="ghost" icon="left" onClick={() => navigate(resultsLink)}>לכל המבחנים</Button>}
        >
          <RankList
            loading={busy}
            tone="success"
            empty="אין ציונים בטווח שנבחר"
            formatValue={(v) => (v == null ? '—' : `${v}%`)}
            items={(d.recentGrades || []).map((g) => ({
              key: g.id,
              label: `${g.name}${g.city ? ` · ${g.city}` : ''}`,
              sub: [[g.examMeta, g.examName].filter(Boolean).join(' · '), formatHebrewDateTime(g.enteredAt)].filter(Boolean).join(' — '),
              value: g.scorePercent,
            }))}
          />
        </Panel>

        <Panel
          title="פילוח לפי עיר"
          icon="flag"
          tone="info"
          actions={(
            <Select
              aria-label="מבחן"
              style={{ minWidth: 150, maxWidth: 220 }}
              value={examId}
              onChange={setExamId}
              placeholder="כל המבחנים"
              options={(d.examOptions || []).map((o) => ({ value: o.id, label: o.label }))}
            />
          )}
        >
          {cities.length || busy
            ? <DonutChart data={cities} loading={busy} centerLabel="נבחנים" formatValue={num} />
            : <EmptyState compact icon="chart" title="אין נתונים" />}
        </Panel>
      </PanelGrid>

      <PanelGrid cols={3}>
        <Panel title="נבחנים לאורך זמן" icon="users">
          <BarChart data={trend(d.membersOverTime)} series={[{ key: 'value', label: 'נבחנים', tone: 'primary' }]} height={200} loading={busy} formatValue={num} />
        </Panel>
        <Panel title="ממוצע ציונים" icon="trend" tone="warning">
          <BarChart data={trend(d.avgScoresOverTime)} series={[{ key: 'value', label: 'ממוצע', tone: 'warning' }]} height={200} loading={busy} formatValue={num} />
        </Panel>
        <Panel title="מגמת דקות IVR" icon="phone" tone="info">
          <BarChart data={trend(d.ivrTrend)} series={[{ key: 'value', label: "דק'", tone: 'info' }]} height={200} loading={busy} formatValue={(v) => `${num(v)} דק'`} />
        </Panel>
      </PanelGrid>
    </>
  );
}
